import * as BABYLON from '@babylonjs/core';
import * as GUI from '@babylonjs/gui';
import { Fixtuur } from '@fixtuur/backpack';
import { hasGeometryLoaded } from './transform';
const getDimensionUnitDisplayName = (unit) => {
    const displayNames = {
        millimetre: 'mm',
        centimetre: 'cm',
        metre: 'm',
    };
    return displayNames[unit];
};
const convertMeasurement = (measurement, unit) => {
    const rendererMeasurement = Fixtuur.Measurement.createMeters(measurement);
    const conversions = {
        millimetre: Fixtuur.Measurement.millimetres,
        centimetre: Fixtuur.Measurement.centimetres,
        metre: Fixtuur.Measurement.meters,
    };
    const converter = conversions[unit];
    return converter(rendererMeasurement);
};
export const drawDimensionLines = (_scene, parentTransform, enabled, primaryColour, measurementUnit, dimensions) => {
    // CLEAN UP OLD DIMENSIONS AND LINES
    const oldGui = _scene.getTextureByName('dimensionsGui');
    oldGui && oldGui.dispose();
    const oldBoundingBoxMesh = _scene.getMeshByName('parentMesh');
    oldBoundingBoxMesh && oldBoundingBoxMesh.dispose();
    const oldLines = _scene.meshes.filter(mesh => mesh.name === 'lineMesh');
    oldLines.forEach(mesh => mesh.dispose());
    // only drawDimensions if geometry has loaded, otherwise they will show as Infinity
    const hasLoaded = hasGeometryLoaded(parentTransform);
    if (enabled && hasLoaded) {
        // SET BOUNDING BOX OF ALL MESHES
        let boundingBoxMesh = new BABYLON.Mesh('parentMesh', _scene);
        let { min, max } = parentTransform.getHierarchyBoundingVectors();
        boundingBoxMesh.setBoundingInfo(new BABYLON.BoundingInfo(min, max));
        const lineColour = BABYLON.Color3.FromHexString(primaryColour);
        const unitLabel = getDimensionUnitDisplayName(measurementUnit);
        // GET VERTICES OF BOUNDING BOX
        const meshPosCenter = boundingBoxMesh.getBoundingInfo().boundingBox.centerWorld;
        const meshSize = boundingBoxMesh.getBoundingInfo().boundingBox.extendSizeWorld;
        const meshLargestAxis = Math.max(meshSize.x, meshSize.y, meshSize.z);
        const padding = 0.07 * meshLargestAxis;
        // negative centres
        const xN = meshPosCenter.x - meshSize.x - padding;
        const yN = meshPosCenter.y - meshSize.y - padding;
        const zN = meshPosCenter.z - meshSize.z - padding;
        // positive centres
        const xP = meshPosCenter.x + meshSize.x + padding;
        const yP = meshPosCenter.y + meshSize.y + padding;
        const zP = meshPosCenter.z + meshSize.z + padding;
        // DRAW LINES BETWEEN VERTICES
        const xLineFloor = [new BABYLON.Vector3(xN, yN, zN), new BABYLON.Vector3(xP, yN, zN)];
        const xLineTop = [new BABYLON.Vector3(xN, yP, zN), new BABYLON.Vector3(xP, yP, zN)];
        const yLineLeft = [new BABYLON.Vector3(xN, yN, zN), new BABYLON.Vector3(xN, yP, zN)];
        const yLineRight = [new BABYLON.Vector3(xP, yN, zN), new BABYLON.Vector3(xP, yP, zN)];
        const zLineLeft = [new BABYLON.Vector3(xN, yN, zN), new BABYLON.Vector3(xN, yN, zP)];
        const zLineRight = [new BABYLON.Vector3(xP, yN, zN), new BABYLON.Vector3(xP, yN, zP)];
        dimensions = dimensions ?? {
            width: meshSize.x * 2,
            height: meshSize.y * 2,
            depth: meshSize.z * 2,
        };
        const lines = [
            {
                label: convertMeasurement(dimensions.width, measurementUnit).toFixed(0) +
                    ` ${unitLabel}`,
                lines: [xLineTop],
                direction: 'y',
            },
            {
                label: convertMeasurement(dimensions.height, measurementUnit).toFixed(0) +
                    ` ${unitLabel}`,
                lines: [yLineLeft],
                direction: 'x',
            },
            {
                label: convertMeasurement(dimensions.depth, measurementUnit).toFixed(0) +
                    ` ${unitLabel}`,
                lines: [zLineLeft],
                direction: 'x',
            },
        ];
        // GUI layer
        const advancedTexture = GUI.AdvancedDynamicTexture.CreateFullscreenUI('dimensionsGui');
        lines.forEach(line => {
            // TODO: get normal for vertices to determine which ones to show ie which is facing camera
            line.lines.forEach(l => {
                // TODO: use gui to draw lines instead of meshes
                const lineMesh = BABYLON.MeshBuilder.CreateLines('Lines', { points: l }, _scene);
                lineMesh.name = 'lineMesh';
                lineMesh.color = lineColour;
                const textContainer = new GUI.Rectangle();
                textContainer.width = '80px';
                textContainer.height = '30px';
                textContainer.cornerRadius = 10;
                textContainer.color = primaryColour;
                textContainer.thickness = 2;
                textContainer.background = 'white';
                advancedTexture.addControl(textContainer);
                textContainer.linkWithMesh(lineMesh);
                textContainer.linkOffsetY = line.direction === 'y' ? -60 : 0;
                textContainer.linkOffsetX = line.direction === 'x' ? -80 : 0;
                const labelText = new GUI.TextBlock();
                labelText.text = line.label;
                labelText.fontSize = '12px';
                labelText.fontFamily = 'Inter Tight';
                textContainer.addControl(labelText);
                const circle = new GUI.Ellipse();
                circle.width = '10px';
                circle.height = '10px';
                circle.color = primaryColour;
                circle.thickness = 2;
                circle.background = 'white';
                advancedTexture.addControl(circle);
                circle.linkWithMesh(lineMesh);
                const labelLine = new GUI.Line();
                labelLine.lineWidth = 2;
                labelLine.color = primaryColour;
                labelLine.dash = [5, 10];
                labelLine.y2 = line.direction === 'y' ? 15 : 0;
                labelLine.linkOffsetY = line.direction === 'y' ? -5 : 0;
                labelLine.x2 = line.direction === 'x' ? 40 : 0;
                labelLine.linkOffsetX = line.direction === 'x' ? -5 : 0;
                advancedTexture.addControl(labelLine);
                labelLine.linkWithMesh(lineMesh);
                labelLine.connectedControl = textContainer;
            });
        });
    }
};
